import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DispatchService from './DispatchService';
import RangeList from './RangeList';
import { thaiTodayKey, nextSevenDays, rangesProblem } from './weeklyHours';

// Public, mobile-first page a buyer (คนกด) opens from their personal link:
// their own status for today and Start / Stop; shift buyers also set their own
// shifts for the next 7 days. The token in the URL is the key; it only reaches
// this one buyer. Thai, because buyers use it.
//
// Every time here is THAI time (what the bot uses). Buyers are in Japan, which is
// 2 hours ahead, so each time is also shown in Japan time to avoid misreading.

const clock = (iso, timeZone) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone });
const thaiAndJapan = (iso) => `${clock(iso, 'Asia/Bangkok')} เวลาไทย (${clock(iso, 'Asia/Tokyo')} ญี่ปุ่น)`;

/** "HH:mm" Thai → "HH:mm" Japan (+2 h; neither country changes its clocks). */
const toJapan = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String((h + 2) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const rangesJapan = (ranges) => ranges.map((r) => `${toJapan(r.start)}–${toJapan(r.end)}`).join(', ');
const rangesThai = (ranges) => ranges.map((r) => `${r.start}–${r.end}`).join(', ');

const RANGE_LABELS = { add: 'เพิ่มช่วงเวลา', off: 'ไม่รับงาน', remove: 'ลบช่วงเวลานี้' };

/** Today's hours (Thai date) from their schedule, in Thai and Japan time. */
const todayHoursText = (me) => {
  const today = nextSevenDays()[0];
  const label = `${me.scheduleType === 'shift' ? 'กะวันนี้' : 'เวลางานวันนี้'} (${today.th})`;
  if (me.scheduleType !== 'shift' && !me.weeklyHours) return `${label}: ยังไม่ได้ตั้งเวลางาน ติดต่อแอดมิน`;
  const ranges = (me.scheduleType === 'shift' ? me.shifts?.[today.key] : me.weeklyHours[thaiTodayKey()]) || [];
  if (!ranges.length) return `${label}: ไม่มีงาน`;
  return `${label}: ${rangesThai(ranges)} เวลาไทย (ญี่ปุ่น ${rangesJapan(ranges)})`;
};

/** Shift buyers: today and the next 6 days, each with its own ranges. */
const ShiftEditor = ({ token, me, onSaved }) => {
  const days = nextSevenDays();
  const [shifts, setShifts] = useState(() => Object.fromEntries(days.map((d) => [d.key, me.shifts?.[d.key] || []])));
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const save = async () => {
    const problem = rangesProblem(shifts);
    if (problem) {
      const day = days.find((d) => d.key === problem.key)?.th || problem.key;
      setError(problem.kind === 'closed'
        ? `${day}: ช่วงเวลาต้องอยู่ระหว่าง 07:00–02:00`
        : `${day}: ช่วงเวลาทับกัน แก้ให้ไม่ทับ หรือลบออก 1 ช่วง`);
      setNote(null);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await DispatchService.saveMyShifts(token, shifts);
      onSaved(res.data.me);
      setNote(res.data.message);
    } catch (err) {
      setError(err?.response?.data?.message || 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 text-left border-t pt-5">
      <p className="font-semibold mb-1">ตารางกะ 7 วัน — เลือกเป็นเวลาไทย</p>
      <p className="text-xs text-gray-500 mb-4">
        วันไหนไม่ใส่กะ = ไม่ได้รับงานวันนั้น · ได้เฉพาะ 07:00–02:00 เวลาไทย (09:00–04:00 ญี่ปุ่น) · ทีละ 30 นาที ·
        ใต้แต่ละวันจะแสดงเวลาญี่ปุ่นให้ดู
      </p>
      <div className="grid gap-3">
        {days.map((d) => (
          <div key={d.key}>
            <p className="text-sm font-medium mb-1">{d.th}</p>
            <RangeList
              idPrefix={`my-${d.key}`}
              ranges={shifts[d.key] || []}
              labels={RANGE_LABELS}
              onChange={(r) => setShifts((prev) => ({ ...prev, [d.key]: r }))}
            />
            {shifts[d.key]?.length > 0 && (
              <p className="text-xs text-blue-700 mt-1">= เวลาญี่ปุ่น {rangesJapan(shifts[d.key])}</p>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="w-full mt-5 rounded-xl py-3 text-base font-semibold text-white bg-blue-600 disabled:opacity-40"
      >
        บันทึกตารางกะ
      </button>
      {note && <p className="text-sm text-green-700 mt-3 text-center">{note}</p>}
      {error && <p className="text-sm text-red-600 mt-3 text-center">{error}</p>}
    </div>
  );
};

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

export default function DispatchBuyerPage() {
  const { token } = useParams();
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await DispatchService.getMe(token);
      setMe(res.data.me);
    } catch (err) {
      setLoadError(errorText(err, 'เปิดหน้านี้ไม่ได้ ลองใหม่อีกครั้ง'));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (start) => {
    try {
      setBusy(true);
      setActionError(null);
      const res = start ? await DispatchService.meStart(token) : await DispatchService.meStop(token);
      setMe(res.data.me);
      setMessage(res.data.message);
    } catch (err) {
      setActionError(errorText(err, 'ทำรายการไม่สำเร็จ ลองใหม่อีกครั้ง'));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-red-600 font-semibold text-lg mb-2">เปิดหน้านี้ไม่ได้</p>
          <p className="text-gray-500 text-sm">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">กำลังโหลด...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow p-6 max-w-sm w-full text-center">
        <p className="text-gray-500 text-sm">รับงานกด Mercari</p>
        <p className="text-xl font-bold mb-3">{me.label}</p>
        <p className="text-sm font-semibold text-amber-800 bg-amber-100 rounded-lg px-3 py-2 mb-4">
          ⏰ เวลาทั้งหมดในหน้านี้เป็น <u>เวลาไทย</u><br />
          ญี่ปุ่นเร็วกว่าไทย 2 ชั่วโมง (ไทย 10:00 = ญี่ปุ่น 12:00)
        </p>

        <div className={`rounded-xl py-5 mb-2 ${me.onlineNow ? 'bg-green-100' : 'bg-gray-100'}`}>
          <p className={`text-3xl font-bold ${me.onlineNow ? 'text-green-700' : 'text-gray-500'}`}>
            {me.onlineNow ? '● รับงานอยู่' : '○ ไม่รับงาน'}
          </p>
          {me.overrideUntil && (
            <p className="text-sm text-gray-600 mt-1">
              {me.override === 'on' ? 'เปิดเอง' : 'หยุดเอง'} ถึง {thaiAndJapan(me.overrideUntil)}
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-5">{todayHoursText(me)}</p>

        {!me.dispatchEnabled ? (
          <p className="text-amber-700 bg-amber-50 rounded-lg p-3 text-sm">ยังไม่ได้เปิดใช้ ติดต่อแอดมิน</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              disabled={busy || me.onlineNow}
              onClick={() => act(true)}
              className="rounded-xl py-4 text-lg font-semibold text-white bg-green-600 disabled:opacity-40"
            >
              เริ่มรับงาน
            </button>
            <button
              type="button"
              disabled={busy || !me.onlineNow}
              onClick={() => act(false)}
              className="rounded-xl py-4 text-lg font-semibold text-white bg-gray-600 disabled:opacity-40"
            >
              หยุดรับงาน
            </button>
          </div>
        )}

        {message && <p className="text-sm text-green-700 mt-4">{message}</p>}
        {actionError && <p className="text-sm text-red-600 mt-4">{actionError}</p>}
        <p className="text-xs text-gray-400 mt-5">
          เริ่ม / หยุด มีผลถึงหมดเวลางานของวันนี้ หรือถึง 02:00 เวลาไทย (04:00 ญี่ปุ่น) แล้วกลับไปตามเวลาปกติ
        </p>

        <Link to={`/dispatch/me/${token}/tracking`} className="block mt-5 rounded-xl py-3 font-semibold text-green-700 border border-green-600">
          กรอกเลขพัสดุ
        </Link>

        {me.scheduleType === 'shift' && <ShiftEditor token={token} me={me} onSaved={setMe} />}
      </div>
    </div>
  );
}
